import { useMemo } from "react";
import {
  useAppItemsStream,
  type AppItemsStreamResult,
  type AppItemsStreamSource,
  type AppItemsStreamStatus,
} from "@/features/published-apps/hooks/useAppItemsStream";
import { apiFetch } from "@/shared/api/apiFetch";

export type PublishedAppItemsStreamStatus = AppItemsStreamStatus;

/**
 * Where an authenticated app view streams its widgets from.
 *
 * Exported because the favourites prefetch buffers under this exact `key` — the view finds that
 * buffer by key, so the two must never drift apart.
 */
export function buildPublishedAppItemsStreamSource(
  appId: number,
  snapshotId: number | null = null,
): AppItemsStreamSource {
  return {
    key: snapshotId === null ? `published:${appId}` : `published:${appId}:snapshot:${snapshotId}`,
    path:
      snapshotId === null
        ? `/v1/published-apps/${appId}/items/data`
        : `/v1/published-apps/${appId}/snapshots/${snapshotId}/items/data`,
    fetcher: apiFetch,
  };
}

/**
 * Streams a published app's chart/table/KPI widgets for the authenticated app view.
 *
 * Thin wrapper over {@link useAppItemsStream}: this route is authenticated (`apiFetch`) and sends no
 * `app`/`filters` frames — the app detail and per-tab filter fields are fetched separately here.
 *
 * With `snapshotId` set, the same hook streams a stored snapshot instead. The server replays the saved
 * document as an identical frame sequence, so nothing downstream changes; a snapshot stream *does*
 * open with an `app` frame, carrying the app's name and description as they were when captured. The
 * source key includes the snapshot id, which is what makes switching snapshots reset and re-open the
 * stream without any extra wiring.
 */
export function usePublishedAppItemsStream(
  appId: number | null,
  snapshotId: number | null = null,
): AppItemsStreamResult {
  const source = useMemo(
    () => (appId === null ? null : buildPublishedAppItemsStreamSource(appId, snapshotId)),
    [appId, snapshotId],
  );

  return useAppItemsStream(source);
}
